/**
 * Community Hooks
 */
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { communityService } from '../services/communityService';
import { queryTags } from '../queryTags';
import { getQueryClient } from '../queryClient';
import type {
  CreateCommunityGroupPayload,
  UpdateCommunityGroupPayload,
  GetCommunityGroupsParams,
  GetCommunityCommentsParams,
} from '../types/community.types';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';

/**
 * Get community groups hook
 */
export function useGetCommunityGroups(
  params?: GetCommunityGroupsParams,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [
      queryTags.community,
      params?.search || '',
      params?.page || 1,
      params?.limit || 10,
    ],
    queryFn: () => communityService.getCommunityGroups(params),
    ...options,
  });
}

/**
 * Get single community group hook
 */
export function useGetCommunityGroup(
  id: string,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.community, id],
    queryFn: () => communityService.getCommunityGroup(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Create community group mutation hook
 */
export function useCreateCommunityGroup(
  options?: UseMutationOptions<any, any, CreateCommunityGroupPayload, any>
) {
  return useApiMutation<any, any, CreateCommunityGroupPayload, any>({
    mutationFn: ((data: CreateCommunityGroupPayload) => communityService.createCommunityGroup(data)) as any,
    onSuccess: (data, variables, context, mutation) => {
      // Always invalidate queries to refresh the list
      getQueryClient().invalidateQueries({ queryKey: [queryTags.community] });
      // Call custom onSuccess if provided
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, mutation);
      }
    },
    onError: options?.onError,
  });
}

/**
 * Update community group mutation hook
 */
export function useUpdateCommunityGroup(
  options?: UseMutationOptions<any, any, { id: string; data: UpdateCommunityGroupPayload }, any>
) {
  return useApiMutation<any, any, { id: string; data: UpdateCommunityGroupPayload }, any>({
    mutationFn: ((variables: { id: string; data: UpdateCommunityGroupPayload }) =>
      communityService.updateCommunityGroup(variables.id, variables.data)) as any,
    onSuccess: (data, variables, context, mutation) => {
      // Always invalidate queries to refresh the list
      getQueryClient().invalidateQueries({ queryKey: [queryTags.community] });
      // Call custom onSuccess if provided
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, mutation);
      }
    },
    onError: options?.onError,
  });
}

/**
 * Delete community group mutation hook
 */
export function useDeleteCommunityGroup(
  options?: UseMutationOptions<any, any, string, any>
) {
  return useApiMutation<any, any, string, any>({
    mutationFn: ((id: string) => communityService.deleteCommunityGroup(id)) as any,
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: [queryTags.community] });
    },
    ...options,
  });
}

/**
 * Get community comments hook
 */
export function useGetCommunityComments(
  params: GetCommunityCommentsParams,
  options?: Omit<UseQueryOptions<any, any, any, any[]>, 'queryKey' | 'queryFn'>
) {
  return useApiQuery({
    queryKey: [queryTags.community, 'comments', params.communityId, params.page || 1, params.limit || 10],
    queryFn: () => communityService.getCommunityComments(params),
    enabled: !!params.communityId,
    ...options,
  });
}

